import React, { useEffect, useMemo, useState } from 'react';
import { AlertTriangle, Pencil } from 'lucide-react';
import Modal from '../../../shared/components/ui/Modal';
import { getBomSyncStatus } from '../../services/api/implementation/impl';
import {
  computeBomMaterialAmount,
  formatBoqBomCurrency,
  formatBomQuantity,
  mapBomSyncStatusUnits
} from '../../utils/boqBomExtract';

const COSTING_INPUT_CLASS =
  'w-full min-w-0 rounded-md border border-[#D1D5DB] px-2 py-1.5 text-[13px] text-[#111827] focus:border-[#2A6A73] focus:outline-none focus:ring-1 focus:ring-[#2A6A73]';

const FOOTER_ACTION_BTN =
  'inline-flex h-10 min-w-[10rem] items-center justify-center rounded-md px-5 text-[14px] font-medium leading-none transition-colors box-border';

export const BOQ_BOM_GENERATED_SUBTITLE = 'Materials and their quantity shown in cards';

export const MOCK_BRICKWORK_COMBINATIONS = [
  {
    id: 1,
    title: 'Combination 1',
    description: 'Standard brickwork — cement, water & fly ash bricks',
    materials: [
      {
        id: 'bricks',
        name: 'Fly ash bricks',
        detail: '230 × 110 × 75mm',
        qty: '14,820',
        unit: 'Nos'
      },
      {
        id: 'cement',
        name: 'Cement (OPC 53)',
        detail: 'Masonry mortar · 1:5 mix',
        qty: '48',
        unit: 'Bags'
      },
      {
        id: 'water',
        name: 'Water',
        detail: 'W/C ratio 0.5 · mixing use',
        qty: '1,200',
        unit: 'Litres'
      }
    ]
  },
  {
    id: 2,
    title: 'Combination 2',
    description: 'Standard brickwork — cement, water & fly ash bricks',
    materials: [
      {
        id: 'bricks',
        name: 'Fly ash bricks',
        detail: '230 × 110 × 75mm',
        qty: '14,820',
        unit: 'Nos'
      },
      {
        id: 'cement',
        name: 'Cement (OPC 53)',
        detail: 'Masonry mortar · 1:5 mix',
        qty: '48',
        unit: 'Bags'
      },
      {
        id: 'water',
        name: 'Water',
        detail: 'W/C ratio 0.5 · mixing use',
        qty: '1,200',
        unit: 'Litres'
      }
    ]
  },
  {
    id: 3,
    title: 'Combination 3',
    description: 'Standard brickwork — cement, water & fly ash bricks',
    materials: [
      {
        id: 'bricks',
        name: 'Fly ash bricks',
        detail: '230 × 110 × 75mm',
        qty: '14,820',
        unit: 'Nos'
      },
      {
        id: 'cement',
        name: 'Cement (OPC 53)',
        detail: 'Masonry mortar · 1:5 mix',
        qty: '48',
        unit: 'Bags'
      },
      {
        id: 'water',
        name: 'Water',
        detail: 'W/C ratio 0.5 · mixing use',
        qty: '1,200',
        unit: 'Litres'
      }
    ]
  },
  {
    id: 4,
    title: 'Combination 4',
    description: 'Standard brickwork — cement, water & fly ash bricks',
    materials: [
      {
        id: 'bricks',
        name: 'Fly ash bricks',
        detail: '230 × 110 × 75mm',
        qty: '14,820',
        unit: 'Nos'
      },
      {
        id: 'cement',
        name: 'Cement (OPC 53)',
        detail: 'Masonry mortar · 1:5 mix',
        qty: '48',
        unit: 'Bags'
      },
      {
        id: 'water',
        name: 'Water',
        detail: 'W/C ratio 0.5 · mixing use',
        qty: '1,200',
        unit: 'Litres'
      }
    ]
  }
];

export const MOCK_BOM_WORK_SECTIONS = [
  {
    id: 'brickwork',
    index: 1,
    title: 'Brickwork',
    boqItems: [
      {
        id: 'mock-brickwork',
        description: 'Standard brickwork — cement, water & fly ash bricks',
        quantityRef: '—',
        combinations: MOCK_BRICKWORK_COMBINATIONS
      }
    ]
  }
];

export const AccuracyRateGauge = ({ value = 82, size = 52 }) => {
  const strokeWidth = 4;
  const radius = (size - strokeWidth * 2) / 2;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference - (value / 100) * circumference;

  return (
    <div className="flex flex-col items-center shrink-0">
      <div className="relative" style={{ width: size, height: size }}>
        <svg className="-rotate-90" width={size} height={size} aria-hidden="true">
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            stroke="#E5E7EB"
            strokeWidth={strokeWidth}
            fill="transparent"
          />
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            stroke="#2A9CB0"
            strokeWidth={strokeWidth}
            fill="transparent"
            strokeDasharray={circumference}
            strokeDashoffset={strokeDashoffset}
            strokeLinecap="round"
          />
        </svg>
        <div className="absolute inset-0 flex items-center justify-center">
          <span className="text-[12px] font-bold text-[#111827] leading-none">{value}%</span>
        </div>
      </div>
      <span className="mt-0.5 text-[10px] font-medium text-[#6B7280]">Accuracy Rate</span>
    </div>
  );
};

const CombinationCard = ({ sectionId, combination, selected, onSelect }) => (
  <label
    className={`block cursor-pointer rounded-lg border bg-white overflow-hidden shadow-[0_1px_2px_rgba(0,0,0,0.04)] transition-colors ${
      selected ? 'border-[#2A9CB0] ring-1 ring-[#2A9CB0]/30' : 'border-[#E5E7EB] hover:border-[#C5CCD3]'
    }`}>
    <div className="flex items-start gap-2.5 bg-[#E8F4F8] px-3.5 py-3 border-b border-[#D1E8ED]">
      <input
        type="radio"
        name={`combination-${sectionId}`}
        checked={selected}
        onChange={onSelect}
        className="mt-0.5 h-4 w-4 shrink-0 accent-[#2A6A73]"
      />
      <div className="min-w-0">
        <p className="text-[14px] font-semibold text-[#111827]">{combination.title}</p>
        <p className="mt-0.5 text-[12px] text-[#6B7280] leading-snug">{combination.description}</p>
      </div>
    </div>
    <div className="overflow-x-auto">
      <table className="w-full text-left text-[13px]">
        <thead>
          <tr className="border-b border-[#F3F4F6] bg-[#FAFBFC]">
            {['Material', 'Qty', 'Unit'].map((header) => (
              <th
                key={header}
                className={`px-3.5 py-2.5 text-[10px] font-semibold uppercase tracking-[0.05em] text-[#9CA3AF] ${
                  header === 'Material' ? 'w-[52%]' : 'w-[24%]'
                }`}>
                {header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {combination.materials.map((material) => (
            <tr key={material.id} className="border-b border-[#F3F4F6] last:border-b-0">
              <td className="px-3.5 py-3">
                <p className="font-semibold text-[#111827]">{material.name}</p>
                <p className="mt-0.5 text-[11px] text-[#9CA3AF]">{material.detail}</p>
              </td>
              <td className="px-3.5 py-3 font-semibold text-[#111827]">{material.qty}</td>
              <td className="px-3.5 py-3 text-[#4B5563]">{material.unit}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </label>
);

const BomBoqItemBlock = ({
  boqItem,
  sectionId,
  selectedCombinationId,
  onSelectCombination
}) => (
  <div className="space-y-3">
    <div className="px-1">
      <p className="text-[13px] font-medium text-[#374151]">{boqItem.description}</p>
      <p className="text-[12px] text-[#6B7280]">BOQ ref: {boqItem.quantityRef}</p>
    </div>
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {boqItem.combinations.map((combination) => (
        <CombinationCard
          key={combination.id}
          sectionId={`${sectionId}-${boqItem.id}`}
          combination={combination}
          selected={selectedCombinationId === combination.id}
          onSelect={() => onSelectCombination(boqItem.id, combination.id)}
        />
      ))}
    </div>
  </div>
);

const BomWorkSection = ({
  section,
  selectedByBoqItem,
  onSelectCombination
}) => (
  <section className="rounded-xl border border-[#E5E7EB] bg-[#F0F2F4] overflow-hidden">
    <div className="flex items-center gap-2.5 min-w-0 bg-white px-4 py-3 border-b border-[#E5E7EB]">
      <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-[#D1D5DB] bg-white text-[13px] font-semibold text-[#374151]">
        {section.index}
      </span>
      <h3 className="text-[15px] font-semibold text-[#111827]">{section.title}</h3>
    </div>
    <div className="p-4 space-y-6">
      {(section.boqItems ?? []).map((boqItem) => (
        <BomBoqItemBlock
          key={boqItem.id}
          boqItem={boqItem}
          sectionId={section.id}
          selectedCombinationId={selectedByBoqItem[boqItem.id]}
          onSelectCombination={onSelectCombination}
        />
      ))}
    </div>
  </section>
);

export const BoqBomGeneratedResults = ({
  bomData,
  sections: sectionsProp,
  selectedByBoqItem: selectedByBoqItemProp,
  onSelectionChange
}) => {
  const sections = bomData?.workSections ?? sectionsProp ?? MOCK_BOM_WORK_SECTIONS;

  const [internalSelections, setInternalSelections] = useState({});
  const isControlled = selectedByBoqItemProp != null;
  const selectedByBoqItem = isControlled ? selectedByBoqItemProp : internalSelections;

  useEffect(() => {
    if (isControlled) return;
    const next = {};
    sections.forEach((section) => {
      (section.boqItems ?? []).forEach((item) => {
        next[item.id] = item.combinations?.[0]?.id ?? null;
      });
    });
    setInternalSelections(next);
  }, [sections, isControlled]);

  const handleSelectCombination = (boqItemId, combinationId) => {
    const next = { ...selectedByBoqItem, [boqItemId]: combinationId };
    if (isControlled) {
      onSelectionChange?.(next);
    } else {
      setInternalSelections(next);
    }
  };

  if (!sections.length) {
    return (
      <div className="max-w-[1100px] w-full mx-auto">
        <p className="text-[14px] text-[#6B7280]">No BOM combinations in the response.</p>
      </div>
    );
  }

  return (
    <div className="max-w-[1100px] w-full mx-auto space-y-5">
      {bomData?.validationSummary && (
        <div className="rounded-xl border border-[#E5E7EB] bg-white px-5 py-4 shadow-[0_1px_2px_rgba(0,0,0,0.04)]">
          <p className="text-[15px] font-semibold text-[#111827]">BOM generated successfully</p>
          <p className="mt-1 text-[13px] text-[#6B7280]">
            {bomData.validationSummary.totalCombinationsGenerated ?? 0} combination groups ·{' '}
            {bomData.materialCount ?? 0} material lines ·{' '}
            {bomData.inventoryMaterialsUsedCount ?? 0} from inventory
          </p>
        </div>
      )}
      {sections.map((section) => (
        <BomWorkSection
          key={section.id}
          section={section}
          selectedByBoqItem={selectedByBoqItem}
          onSelectCombination={handleSelectCombination}
        />
      ))}
    </div>
  );
};

/** Inventory items for a material's picker, closest name matches first. */
const rankInventoryOptions = (inventoryOptions, materialName) => {
  const words = String(materialName || '')
    .toLowerCase()
    .split(/[^a-z0-9]+/)
    .filter((w) => w.length > 2);
  const score = (item) =>
    words.filter((w) => String(item.name).toLowerCase().includes(w)).length;
  // Active items first, then best name match.
  const activeRank = (item) => (item.isActive === false ? 1 : 0);
  return [...inventoryOptions].sort(
    (a, b) =>
      activeRank(a) - activeRank(b) ||
      score(b) - score(a) ||
      String(a.name).localeCompare(String(b.name))
  );
};

/** Links a saved BOM material to a project inventory item (works on read-only BOMs too). */
const InventoryLinkCell = ({ material, inventoryOptions, onLinkInventory, isLinking }) => {
  const options = useMemo(
    () => rankInventoryOptions(inventoryOptions ?? [], material.name),
    [inventoryOptions, material.name]
  );
  if (!onLinkInventory) return null;
  const linked = material.inventoryId != null;
  return (
    <td className="px-4 py-3">
      <select
        value={linked ? String(material.inventoryId) : ''}
        disabled={isLinking || !options.length}
        onChange={(e) => onLinkInventory(material, e.target.value ? Number(e.target.value) : null)}
        aria-label={`Inventory item for ${material.name}`}
        className={`w-full max-w-[220px] rounded-md border px-2 py-1.5 text-[12px] focus:border-[#2A6A73] focus:outline-none focus:ring-1 focus:ring-[#2A6A73] disabled:opacity-60 ${
          linked ? 'border-[#D1D5DB] text-[#111827]' : 'border-[#F59E0B] bg-[#FFFBEB] text-[#92400E]'
        }`}>
        <option value="">{options.length ? 'Not linked — pick item' : 'No inventory items'}</option>
        {options.map((item) => (
          <option key={item.id} value={String(item.id)}>
            {item.name}
            {item.unit ? ` (${item.unit})` : ''}
            {item.isActive === false ? ' — Inactive' : ''}
          </option>
        ))}
      </select>
    </td>
  );
};

const CostingMaterialTable = ({
  combinationName,
  materials,
  onSaveMaterial,
  savingRowId,
  readOnly = false,
  inventoryOptions,
  onLinkInventory,
  linkingRowId
}) => {
  const [editingRowId, setEditingRowId] = useState(null);
  const [editOriginal, setEditOriginal] = useState(null);
  const [draft, setDraft] = useState(null);

  const startEdit = (material) => {
    const snapshot = { ...material };
    setEditingRowId(material.rowId);
    setEditOriginal(snapshot);
    setDraft(snapshot);
  };

  const cancelEdit = () => {
    setEditingRowId(null);
    setEditOriginal(null);
    setDraft(null);
  };

  const updateDraft = (field, value) => {
    setDraft((prev) => {
      if (!prev) return prev;
      const next = { ...prev, [field]: value };
      if (field === 'quantity' || field === 'unitRate') {
        const amount = computeBomMaterialAmount(
          field === 'quantity' ? value : next.quantity,
          field === 'unitRate' ? value : next.unitRate
        );
        if (amount != null) next.amount = amount;
      }
      return next;
    });
  };

  const handleSave = async () => {
    if (!draft || !editOriginal || !onSaveMaterial) return;
    const saved = await onSaveMaterial(editingRowId, editOriginal, draft);
    if (saved !== false) cancelEdit();
  };

  return (
    <div className="rounded-lg border border-[#E5E7EB] bg-white overflow-hidden shadow-[0_1px_2px_rgba(0,0,0,0.04)]">
      <div className="bg-[#E8F4F8] px-4 py-3 border-b border-[#D1E8ED]">
        <p className="text-[14px] font-semibold text-[#111827]">{combinationName}</p>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full min-w-[720px] text-left text-[13px]">
          <thead>
            <tr className="border-b border-[#E5E7EB] bg-[#F9FAFB]">
              {[
                'Material',
                ...(onLinkInventory ? ['Inventory item'] : []),
                'Qty',
                'Unit',
                'Rate',
                'Amount',
                ...(readOnly ? [] : ['Actions'])
              ].map((header) => (
                <th
                  key={header}
                  className="px-4 py-3 text-[11px] font-semibold uppercase tracking-[0.04em] text-[#6B7280] whitespace-nowrap">
                  {header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {materials.map((material) => {
              const isEditing = editingRowId === material.rowId;
              const isSaving = savingRowId === material.rowId;

              if (isEditing && draft) {
                return (
                  <tr key={material.rowId} className="border-b border-[#F3F4F6] bg-[#F4F9FA]">
                    <td className="px-4 py-3.5">
                      <p className="font-semibold text-[#111827]">{material.name}</p>
                      {material.detail ? (
                        <p className="mt-0.5 text-[11px] text-[#9CA3AF]">{material.detail}</p>
                      ) : null}
                    </td>
                    <InventoryLinkCell
                      material={material}
                      inventoryOptions={inventoryOptions}
                      onLinkInventory={onLinkInventory}
                      isLinking={linkingRowId === material.rowId}
                    />
                    <td className="px-4 py-3">
                      <input
                        type="number"
                        step="any"
                        value={draft.quantity ?? ''}
                        onChange={(e) => updateDraft('quantity', e.target.value)}
                        className={COSTING_INPUT_CLASS}
                      />
                    </td>
                    <td className="px-4 py-3 text-[#4B5563] whitespace-nowrap">{draft.unit}</td>
                    <td className="px-4 py-3">
                      <input
                        type="number"
                        step="any"
                        value={draft.unitRate ?? ''}
                        onChange={(e) => updateDraft('unitRate', e.target.value)}
                        className={COSTING_INPUT_CLASS}
                      />
                    </td>
                    <td className="px-4 py-3">
                      <input
                        type="number"
                        step="any"
                        value={draft.amount ?? ''}
                        onChange={(e) => updateDraft('amount', e.target.value)}
                        className={COSTING_INPUT_CLASS}
                        title="Recalculates when qty or rate change; override if needed"
                      />
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap items-center gap-2">
                        <button
                          type="button"
                          onClick={handleSave}
                          disabled={isSaving}
                          className="rounded-md bg-[#2A6A73] px-3 py-1.5 text-[12px] font-medium text-white hover:bg-[#245a62] disabled:opacity-50">
                          {isSaving ? 'Saving…' : 'Save'}
                        </button>
                        <button
                          type="button"
                          onClick={cancelEdit}
                          disabled={isSaving}
                          className="rounded-md border border-[#D1D5DB] bg-white px-3 py-1.5 text-[12px] font-medium text-[#374151] hover:bg-[#F9FAFB]">
                          Cancel
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              }

              return (
                <tr key={material.rowId} className="border-b border-[#F3F4F6] last:border-b-0">
                  <td className="px-4 py-3.5">
                    <p className="font-semibold text-[#111827]">{material.name}</p>
                    {material.detail ? (
                      <p className="mt-0.5 text-[11px] text-[#9CA3AF]">{material.detail}</p>
                    ) : null}
                  </td>
                  <InventoryLinkCell
                    material={material}
                    inventoryOptions={inventoryOptions}
                    onLinkInventory={onLinkInventory}
                    isLinking={linkingRowId === material.rowId}
                  />
                  <td className="px-4 py-3.5 font-semibold text-[#111827] whitespace-nowrap">
                    {formatBomQuantity(material.quantity)}
                  </td>
                  <td className="px-4 py-3.5 text-[#4B5563] whitespace-nowrap">{material.unit}</td>
                  <td className="px-4 py-3.5 text-[#4B5563] whitespace-nowrap">
                    {material.unitRate != null ? formatBoqBomCurrency(material.unitRate) : '—'}
                  </td>
                  <td className="px-4 py-3.5 font-semibold text-[#111827] whitespace-nowrap">
                    {material.amount != null ? formatBoqBomCurrency(material.amount) : '—'}
                  </td>
                  {!readOnly ? (
                    <td className="px-4 py-3.5">
                      <button
                        type="button"
                        onClick={() => startEdit(material)}
                        disabled={Boolean(editingRowId) || isSaving}
                        className="flex h-8 w-8 items-center justify-center rounded-md border border-[#E5E7EB] text-[#6B7280] hover:bg-[#F9FAFB] disabled:opacity-40"
                        aria-label={`Edit ${material.name}`}>
                        <Pencil size={15} strokeWidth={1.75} />
                      </button>
                    </td>
                  ) : null}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const CostingBoqItemBlock = ({
  boqItem,
  onSaveMaterial,
  savingRowId,
  readOnly,
  inventoryOptions,
  onLinkInventory,
  linkingRowId
}) => (
  <div className="space-y-3">
    <div className="px-1">
      <p className="text-[13px] font-medium text-[#374151]">{boqItem.description}</p>
      <p className="text-[12px] text-[#6B7280]">
        Selected: {boqItem.selectedCombinationName}
        {boqItem.totalCost?.grandTotal != null && (
          <span className="ml-2 font-semibold text-[#111827]">
            · Total {formatBoqBomCurrency(boqItem.totalCost.grandTotal)}
          </span>
        )}
      </p>
    </div>
    <CostingMaterialTable
      combinationName={boqItem.selectedCombinationName}
      materials={boqItem.materials}
      onSaveMaterial={onSaveMaterial}
      savingRowId={savingRowId}
      readOnly={readOnly}
      inventoryOptions={inventoryOptions}
      onLinkInventory={onLinkInventory}
      linkingRowId={linkingRowId}
    />
  </div>
);

const CostingWorkSection = ({
  section,
  onSaveMaterial,
  savingRowId,
  readOnly,
  inventoryOptions,
  onLinkInventory,
  linkingRowId
}) => (
  <section className="rounded-xl border border-[#E5E7EB] bg-[#F0F2F4] overflow-hidden">
    <div className="flex items-center gap-2.5 bg-white px-4 py-3 border-b border-[#E5E7EB]">
      <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-[#D1D5DB] bg-white text-[13px] font-semibold text-[#374151]">
        {section.index}
      </span>
      <h3 className="text-[15px] font-semibold text-[#111827]">{section.title}</h3>
    </div>
    <div className="p-4 space-y-6">
      {(section.boqItems ?? []).map((boqItem) => (
        <CostingBoqItemBlock
          key={boqItem.id}
          boqItem={boqItem}
          onSaveMaterial={onSaveMaterial}
          savingRowId={savingRowId}
          readOnly={readOnly}
          inventoryOptions={inventoryOptions}
          onLinkInventory={onLinkInventory}
          linkingRowId={linkingRowId}
        />
      ))}
    </div>
  </section>
);

export const BoqBomCostingResults = ({
  costingData,
  onSaveMaterial,
  savingRowId,
  readOnly = false,
  inventoryOptions,
  onLinkInventory,
  linkingRowId
}) => {
  const sections = costingData?.workSections ?? [];
  const allMaterials = sections.flatMap((section) =>
    (section.boqItems ?? []).flatMap((boqItem) => boqItem.materials ?? [])
  );
  const unlinkedCount = allMaterials.filter((m) => m.inventoryId == null).length;

  if (!sections.length) {
    return (
      <div className="max-w-[1100px] w-full mx-auto">
        <p className="text-[14px] text-[#6B7280]">No priced materials in the response.</p>
      </div>
    );
  }

  return (
    <div className="max-w-[1100px] w-full mx-auto space-y-5">
      <div className="rounded-xl border border-[#E5E7EB] bg-white px-5 py-4 shadow-[0_1px_2px_rgba(0,0,0,0.04)]">
        <p className="text-[15px] font-semibold text-[#111827]">Costing applied · BOM saved</p>
        <p className="mt-1 text-[13px] text-[#6B7280]">
          {costingData.materialCount ?? 0} materials priced · Project total{' '}
          <span className="font-semibold text-[#111827]">
            {formatBoqBomCurrency(costingData.totalProjectCost)}
          </span>
          {costingData.summary?.highestCostDriverWorkType && (
            <span className="block mt-1">
              Highest cost driver: {costingData.summary.highestCostDriverWorkType}
            </span>
          )}
        </p>
      </div>
      {onLinkInventory && unlinkedCount > 0 ? (
        <div className="flex items-start gap-2.5 rounded-xl border border-[#FCD34D] bg-[#FFFBEB] px-5 py-3.5 text-[13px] text-[#92400E]">
          <AlertTriangle size={16} className="mt-0.5 shrink-0" />
          <p>
            {inventoryOptions && inventoryOptions.length === 0
              ? 'This project has no inventory items, so BOM materials cannot be synced to tasks. Add items in Inventory, then link each material below.'
              : `${unlinkedCount} of ${allMaterials.length} materials are not linked to project inventory and will be skipped when syncing with tasks. Pick an inventory item for each below.`}
          </p>
        </div>
      ) : null}
      {sections.map((section) => (
        <CostingWorkSection
          key={section.id}
          section={section}
          onSaveMaterial={onSaveMaterial}
          savingRowId={savingRowId}
          readOnly={readOnly}
          inventoryOptions={inventoryOptions}
          onLinkInventory={onLinkInventory}
          linkingRowId={linkingRowId}
        />
      ))}
    </div>
  );
};

export const BomSyncUnitModal = ({
  isOpen,
  projectId,
  accommodationId,
  accommodationName,
  onClose,
  onConfirm,
  isSyncing = false
}) => {
  const [units, setUnits] = useState([]);
  const [selectedUnitId, setSelectedUnitId] = useState('');
  const [isLoadingUnits, setIsLoadingUnits] = useState(false);
  const [loadError, setLoadError] = useState(null);

  useEffect(() => {
    if (!isOpen) {
      setSelectedUnitId('');
      setLoadError(null);
      return;
    }

    if (!accommodationId) {
      setUnits([]);
      setLoadError('Accommodation is missing.');
      return;
    }

    let cancelled = false;
    setIsLoadingUnits(true);
    setLoadError(null);

    getBomSyncStatus(accommodationId)
      .then((data) => {
        if (cancelled) return;
        const mapped = mapBomSyncStatusUnits(data);
        setUnits(mapped);
        setSelectedUnitId(mapped[0]?.id != null ? String(mapped[0].id) : '');
        if (!mapped.length) {
          const allUnits = data?.data?.units ?? data?.units ?? [];
          setLoadError(
            allUnits.length
              ? 'All units for this accommodation are already synced with live tasks.'
              : 'No units are linked to this independent accommodation.'
          );
        }
      })
      .catch(() => {
        if (cancelled) return;
        setUnits([]);
        setLoadError('Failed to load units. Please try again.');
      })
      .finally(() => {
        if (!cancelled) setIsLoadingUnits(false);
      });

    return () => {
      cancelled = true;
    };
  }, [isOpen, accommodationId]);

  if (!isOpen) return null;

  const handleConfirm = () => {
    if (!selectedUnitId) return;
    onConfirm(Number(selectedUnitId));
  };

  return (
    <Modal name="Select Unit" width={36} onClose={onClose}>
      <div className="space-y-4">
        <p className="text-[14px] text-[#4B5563] leading-relaxed">
          Choose which unit to sync the BOM for{' '}
          <span className="font-semibold text-[#111827]">{accommodationName}</span> with its live
          task.
        </p>

        {isLoadingUnits ? (
          <p className="text-[13px] text-[#6B7280]">Loading units…</p>
        ) : loadError ? (
          <p className="text-[13px] text-[#B45309]">{loadError}</p>
        ) : (
          <div>
            <label htmlFor="bom-sync-unit-select" className="block text-[13px] font-medium text-[#374151] mb-1.5">
              Unit
            </label>
            <select
              id="bom-sync-unit-select"
              value={selectedUnitId}
              onChange={(e) => setSelectedUnitId(e.target.value)}
              className="w-full rounded-md border border-[#D1D5DB] bg-white px-3 py-2.5 text-[14px] text-[#111827] focus:border-[#2A6A73] focus:outline-none focus:ring-1 focus:ring-[#2A6A73]">
              <option value="" disabled>
                Select Unit
              </option>
              {units.map((unit) => (
                <option key={unit.id} value={unit.id}>
                  {unit.label}
                </option>
              ))}
            </select>
          </div>
        )}

        <div className="flex justify-end gap-3 pt-2">
          <button
            type="button"
            onClick={onClose}
            disabled={isSyncing}
            className="rounded-md border border-[#D1D5DB] bg-white px-4 py-2 text-[13px] font-medium text-[#374151] hover:bg-[#F9FAFB] disabled:opacity-50">
            Cancel
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={isSyncing || isLoadingUnits || !selectedUnitId || Boolean(loadError)}
            className="rounded-md bg-[#2A6A73] px-4 py-2 text-[13px] font-medium text-white hover:bg-[#245a62] disabled:opacity-50">
            {isSyncing ? 'Syncing…' : 'Sync with task'}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export const BoqBomGeneratedFooter = ({
  collapsed,
  materialCount = 5,
  aiAssumedCount = 2,
  onCheckInventory,
  isApplyingCosting = false,
  checkInventoryLabel = 'Check inventory list',
  isSyncingWithTask = false,
  syncDisabled = false,
  showActionButton = true
}) => (
  <div
    className={`fixed bottom-0 right-0 z-[5] bg-white border-t border-[#E5E7EB] px-6 md:px-8 py-4 shadow-[0_-2px_8px_rgba(0,0,0,0.04)] transition-all duration-300 ease-in-out ${
      collapsed ? 'left-[76px]' : 'left-64'
    }`}>
    <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
      <div className="flex flex-wrap items-center gap-6 sm:gap-10">
        <p className="text-[14px] font-semibold text-[#111827]">
          <span className="text-[#2A6A73]">{materialCount}</span> items Materials
        </p>
        <p className="text-[14px] font-semibold text-[#111827]">
          <span className="text-[#2A6A73]">{aiAssumedCount}</span> values AI Assumed
        </p>
      </div>
      {showActionButton ? (
        <button
          type="button"
          onClick={onCheckInventory}
          disabled={isApplyingCosting || isSyncingWithTask || syncDisabled}
          className={`${FOOTER_ACTION_BTN} border border-transparent bg-[#2A6A73] text-white hover:bg-[#245a62] shrink-0 disabled:opacity-50 disabled:cursor-not-allowed`}>
          {isApplyingCosting
            ? 'Applying costing…'
            : isSyncingWithTask
              ? 'Syncing…'
              : checkInventoryLabel}
        </button>
      ) : null}
    </div>
  </div>
);
